<script setup lang="ts">
import type { NavActions } from '~~/shared/types/navigation'

/**
 * Right column of the mega menu - "By Need" / Actions
 * Displays contextual action links (e.g., "I need to...")
 * Optional featured item at the bottom for promotions/special links
 *
 * Links with href pattern `/contact?need=xxx` will trigger the Intent Capture Modal
 * instead of navigating, providing a smoother experience for high-intent users.
 */

defineProps<{
  actions: NavActions
}>()

const emit = defineEmits<{
  linkClick: []
}>()

const { isIntentLink, handleLink } = useIntentModal()

/**
 * Handle intent link click - opens modal and closes menu
 */
function onIntentClick(href: string) {
  handleLink(href)
  emit('linkClick')
}

/**
 * Handle regular link click - just closes menu
 */
function onRegularLinkClick() {
  emit('linkClick')
}
</script>

<template>
  <div class="flex flex-col">
    <!-- Section title -->
    <h4 class="text-sm font-semibold text-primary-500">
      {{ actions.title }}
    </h4>

    <!-- Action links -->
    <ul class="mt-5 space-y-4">
      <li v-for="link in actions.links" :key="link.href">
        <!-- Intent links: use button to prevent navigation -->
        <button
          v-if="isIntentLink(link.href)"
          type="button"
          class="group inline-flex items-center gap-2 text-sm text-slate-700 transition-colors hover:text-primary-500"
          @click="onIntentClick(link.href)"
        >
          <span>{{ link.label }}</span>
          <Icon
            name="lucide:arrow-right"
            size="16"
            class="transition-transform duration-200 group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </button>
        <!-- Regular links: use NuxtLink for navigation -->
        <NuxtLink
          v-else
          :to="link.href"
          :target="link.isExternal ? '_blank' : undefined"
          :rel="link.isExternal ? 'noopener noreferrer' : undefined"
          class="group inline-flex items-center gap-2 text-sm text-slate-700 transition-colors hover:text-primary-500"
          @click="onRegularLinkClick"
        >
          <span>{{ link.label }}</span>
          <Icon
            name="lucide:arrow-right"
            size="16"
            class="transition-transform duration-200 group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </NuxtLink>
      </li>
    </ul>

    <!-- Featured item (optional) -->
    <div v-if="actions.featured" class="mt-6 border-t border-neutral-200 pt-4">
      <NuxtLink
        :to="actions.featured.href"
        class="group inline-flex items-center gap-2 text-sm font-bold text-primary-500 transition-colors hover:text-primary-600"
        @click="emit('linkClick')"
      >
        <span class="uppercase">{{ actions.featured.label }}</span>
        <Icon
          name="lucide:arrow-right"
          size="16"
          class="transition-transform duration-200 group-hover:translate-x-0.5"
          aria-hidden="true"
        />
      </NuxtLink>
      <p
        v-if="actions.featured.description"
        class="mt-1 text-xs text-neutral-500"
      >
        {{ actions.featured.description }}
      </p>
    </div>
  </div>
</template>
